import { useCallback, useMemo, useState } from 'react';
import type { IteroOrder, IteroPatient } from '../../../../services/iteroBrowse';
import { NO_SELECTION, type IteroPatientRef } from './iteroBrowserRoute';
import { belongsToChart } from './orderQuery';
import type { OrderTabId } from './order-view/orderTabIds';
import type { SlideDirection } from './pane/paneMotion';
import type { IteroTabNav } from './useIteroBrowserNavigation';
import {
    ORDER_UNAVAILABLE_TEXT,
    PATIENT_UNAVAILABLE_TEXT,
    useDropUnavailable,
    useRestoredOrder,
    useRestoredPatient,
    type Restored
} from './useRestoredSelection';

export interface PaneMotion {
    slide: SlideDirection;
    // The order just left, so its timeline card can take focus back.
    returnFocusRxId: string | null;
}

interface ViewMotion extends PaneMotion {
    viewKey: string;
}

export interface PatientSelection {
    selectedId: string | null;
    patient: Restored<IteroPatient>;
    // The scan drilled into from the patient's timeline; null on the patient profile.
    orderTab: OrderTabId | null;
    order: Restored<IteroOrder>;
    viewKey: string;
    motion: PaneMotion;
    selectPatient: (patient: IteroPatient) => void;
    openOrder: (order: IteroOrder) => void;
    stepBack: () => void;
    setOrderTab: (tab: OrderTabId) => void;
}

const STILL: PaneMotion = { slide: 'none', returnFocusRxId: null };
const MISSING = { kind: 'missing' } as const;

const toRef = (patient: IteroPatient): IteroPatientRef => ({
    id: patient.id,
    chartNumber: patient.chartNumber
});

function findPatient(
    ref: IteroPatientRef | null,
    known: IteroPatient | null,
    rows: readonly IteroPatient[]
): IteroPatient | null {
    if (ref === null) return null;
    if (known?.id === ref.id) return known;
    return rows.find((row) => row.id === ref.id) ?? null;
}

// A scan opened by link must belong to the patient beside it, or the pane would pair the wrong records.
function useDrilledOrder(
    patient: Restored<IteroPatient>,
    rxId: string | null,
    known: IteroOrder | null
): Restored<IteroOrder> {
    const local = known?.rxId === rxId ? known : null;
    const restored = useRestoredOrder(patient.kind === 'ready' ? rxId : null, local);
    const chart = patient.kind === 'ready' ? patient.value.chartNumber : '';
    if (restored.kind !== 'ready' || local !== null) return restored;
    return belongsToChart(restored.value, chart) ? restored : MISSING;
}

// The URL holds which patient and scan are open; the records come from the click, the list or a lookup.
export function usePatientSelection(
    nav: IteroTabNav,
    rows: readonly IteroPatient[]
): PatientSelection {
    const { patient: ref, order: orderRef } = nav.selection;
    const [knownPatient, setKnownPatient] = useState<IteroPatient | null>(null);
    const [knownOrder, setKnownOrder] = useState<IteroOrder | null>(null);
    const [motion, setMotion] = useState<ViewMotion | null>(null);
    const patient = useRestoredPatient(ref, findPatient(ref, knownPatient, rows));
    const order = useDrilledOrder(patient, orderRef?.rxId ?? null, knownOrder);
    const viewKey = orderRef ? `order:${orderRef.rxId}` : `patient:${ref?.id ?? ''}`;

    const dropPatient = useCallback(() => nav.replace(NO_SELECTION), [nav]);
    const dropOrder = useCallback(() => nav.replace({ patient: ref, order: null }), [nav, ref]);
    useDropUnavailable(patient, ref?.id ?? null, PATIENT_UNAVAILABLE_TEXT, dropPatient);
    useDropUnavailable(order, orderRef?.rxId ?? null, ORDER_UNAVAILABLE_TEXT, dropOrder);

    const selectPatient = useCallback(
        (next: IteroPatient) => {
            if (next.id === ref?.id && orderRef === null) return;
            setKnownPatient(next);
            nav.push({ patient: toRef(next), order: null });
        },
        [nav, ref, orderRef]
    );

    const openOrder = useCallback(
        (next: IteroOrder) => {
            if (ref === null) return;
            setKnownOrder(next);
            setMotion({ viewKey: `order:${next.rxId}`, slide: 'forward', returnFocusRxId: null });
            nav.push({ patient: ref, order: { rxId: next.rxId, tab: 'details' } });
        },
        [nav, ref]
    );

    const stepBack = useCallback(() => {
        if (ref && orderRef) {
            const back = { slide: 'back', returnFocusRxId: orderRef.rxId } as const;
            setMotion({ viewKey: `patient:${ref.id}`, ...back });
        }
        nav.stepBack();
    }, [nav, ref, orderRef]);

    const setOrderTab = useCallback(
        (tab: OrderTabId) => {
            if (ref && orderRef) nav.replace({ patient: ref, order: { ...orderRef, tab } });
        },
        [nav, ref, orderRef]
    );

    return useMemo(
        () => ({
            selectedId: ref?.id ?? null,
            patient,
            orderTab: orderRef?.tab ?? null,
            order,
            viewKey,
            motion: motion?.viewKey === viewKey ? motion : STILL,
            selectPatient,
            openOrder,
            stepBack,
            setOrderTab
        }),
        [
            ref,
            patient,
            orderRef,
            order,
            viewKey,
            motion,
            selectPatient,
            openOrder,
            stepBack,
            setOrderTab
        ]
    );
}
